import { styled as muiStyled, css } from '@mui/material/styles';
import React from 'react';
import { useForm } from 'react-hook-form';

import Arranger from '../base/arranger';
import Button from '../base/button';
import { Spacer, spc } from '../base/spacer';
import Text from '../base/text';
import TextField from '../base/text-field';
import { useDashboardDataContext } from '../project/dashboard/dashboard-context';

import yup from '~/client/helpers/yup-extended';
import {
  muiError,
  useYupValidationResolver,
} from '~/client/hooks/yup-validation-resolver';
import * as GQL from '~/graphql/generated/client';
import { useRenameProjectMutation } from '~/graphql/generated/client';

interface Props {
  className?: string;
  projectName: string;
  id: string;
}
interface InputData {
  name: string;
}

const validationSchema = yup.object().shape({
  name: yup.string().trim().required('Project name is required.'),
});

const RenameProjectImpl: React.FC<Props> = ({
  className,
  projectName,
  id: _id,
}) => {
  const { project } = useDashboardDataContext();
  const [isEditing, setIsEditing] = React.useState<boolean>(false);
  const resolver = useYupValidationResolver(validationSchema);
  const { register, formState, handleSubmit, reset } = useForm<InputData>({
    mode: 'onChange',
    resolver,
  });
  const [renameProject, { loading }] = useRenameProjectMutation();
  const { errors } = formState;

  // Check if project is from Asenion source
  const allowEdit = project?.source === GQL.Source.Asenion;

  const onSubmit = async (input: InputData) => {
    if (isEditing && input.name !== projectName) {
      try {
        await renameProject({
          variables: { input: { newProjectName: input.name, _id } },
        }).then(() => {
          setIsEditing(false);
        });
      } catch (error) {}
      return reset();
    }
  };

  return (
    <div className={className}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Arranger dir="column" justify="space-between" alignItems="center">
          {!isEditing ? (
            <>
              <Text sz="h1">{projectName}</Text>
            </>
          ) : (
            <TextField
              {...register('name')}
              {...muiError(errors, 'name')}
              required
              fullWidth
              defaultValue={projectName}
            />
          )}

          <Spacer mr={spc.l} />
          <Arranger alignItems="center">
            {allowEdit && (
              <Button
                loading={loading}
                variant={`${isEditing ? 'contained' : 'text'}`}
                onClick={() => setIsEditing(true)}
                type="submit"
              >
                {isEditing ? `Save` : `Edit`}
              </Button>
            )}
            {isEditing && (
              <Button variant="text" onClick={() => setIsEditing(false)}>
                Cancel
              </Button>
            )}
          </Arranger>
        </Arranger>
      </form>
    </div>
  );
};

export const RenameProject = muiStyled(RenameProjectImpl)(({ theme }) => {
  return css``;
});
